<template>
  <div
    class="w-full rounded-lg bg-neutral-light-default px-2 py-1 card-enter"
    :class="scrollable ? 'flex min-h-20 flex-col overflow-hidden' : 'h-fit shrink-0'"
    :style="{ animationDelay: `${index * 60}ms` }"
  >
    <div class="text-m-medium text-black-white mb-1 shrink-0">{{ title }}</div>

    <ScrollArea
      v-if="scrollable"
      ref="scrollAreaRef"
      class="card-scroll-body min-h-0 flex-1 text-s-medium text-text-glass-default"
      size="sm"
      :style="maskVars"
    >
      <div
        v-for="(item, itemIndex) in data"
        :key="`${item.name}-${itemIndex}`"
        class="flex items-start"
      >
        <span class="shrink-0 whitespace-nowrap">{{ item.name }}：</span>
        <span class="inline-flex flex-1 items-center gap-1 min-w-0">
          <Icon v-if="item.valueIcon" :icon="item.valueIcon" class="size-4 shrink-0 mt-px" />
          <span
            v-if="item.value !== undefined && item.value !== null && item.value !== ''"
            class="min-w-0 break-words whitespace-normal"
          >
            {{ item.value }}
          </span>
        </span>
      </div>
    </ScrollArea>

    <div v-else class="text-s-medium text-text-glass-default">
      <div
        v-for="(item, itemIndex) in data"
        :key="`${item.name}-${itemIndex}`"
        class="flex items-start"
      >
        <span class="shrink-0 whitespace-nowrap">{{ item.name }}：</span>
        <span class="inline-flex flex-1 items-center gap-1 min-w-0">
          <Icon v-if="item.valueIcon" :icon="item.valueIcon" class="size-4 shrink-0" />
          <span
            v-if="item.value !== undefined && item.value !== null && item.value !== ''"
            class="min-w-0 break-words whitespace-normal"
          >
            {{ item.value }}
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, nextTick, onBeforeUnmount, onMounted, shallowRef, watch } from 'vue'
  import { Icon, ScrollArea } from '@rapoo/element'

  export type OverviewCardItem = {
    name: string
    value: string | number
    valueIcon?: string
  }

  const FADE_THRESHOLD = 3

  const props = withDefaults(
    defineProps<{
      data: ReadonlyArray<OverviewCardItem>
      title: string
      index?: number // 用于错落延迟，由父组件传入卡片在整体列表中的顺序
      scrollable?: boolean
    }>(),
    {
      index: 0,
      scrollable: false,
    },
  )

  const scrollAreaRef = shallowRef<{ $el?: HTMLElement } | null>(null)
  const viewportEl = shallowRef<HTMLElement | null>(null)
  const atBottom = shallowRef(true)
  let resizeObserver: ResizeObserver | null = null

  const maskVars = computed(() => {
    if (!props.scrollable) return undefined
    return {
      '--fade-alpha-bottom': atBottom.value ? 0 : 1,
    }
  })

  function getViewport() {
    const root = scrollAreaRef.value?.$el
    if (!(root instanceof HTMLElement)) return null
    return root.querySelector<HTMLElement>('[data-reka-scroll-area-viewport]')
  }

  function checkFade() {
    if (!props.scrollable) {
      atBottom.value = true
      return
    }

    const el = viewportEl.value ?? getViewport()
    if (!el) return

    const maxScroll = el.scrollHeight - el.clientHeight
    if (maxScroll <= FADE_THRESHOLD) {
      atBottom.value = true
      return
    }

    atBottom.value = maxScroll - el.scrollTop <= FADE_THRESHOLD
  }

  function unbindViewport() {
    viewportEl.value?.removeEventListener('scroll', checkFade)
    resizeObserver?.disconnect()
    resizeObserver = null
    viewportEl.value = null
  }

  function bindViewport() {
    unbindViewport()
    if (!props.scrollable) return

    const el = getViewport()
    if (!el) return

    viewportEl.value = el
    el.addEventListener('scroll', checkFade, { passive: true })
    resizeObserver = new ResizeObserver(() => {
      checkFade()
    })
    resizeObserver.observe(el)
  }

  async function refreshFade() {
    await nextTick()
    bindViewport()
    checkFade()
  }

  onMounted(() => {
    void refreshFade()
  })

  watch(
    () => [props.scrollable, props.data.length] as const,
    () => {
      void refreshFade()
    },
  )

  watch(scrollAreaRef, (inst) => {
    if (!inst) return
    void refreshFade()
  })

  onBeforeUnmount(() => {
    unbindViewport()
  })
</script>

<style lang="scss" scoped>
  @property --fade-alpha-bottom {
    syntax: '<number>';
    inherits: true;
    initial-value: 0;
  }

  .card-enter {
    animation: cardIn 360ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  .card-scroll-body {
    --fade-alpha-bottom: 0;
    transition: --fade-alpha-bottom 0.3s ease;

    :deep([data-reka-scroll-area-viewport]) {
      overscroll-behavior: contain;
      -webkit-mask-image: linear-gradient(
        to bottom,
        #000 0,
        #000 calc(100% - 32px),
        rgba(0, 0, 0, calc(1 - var(--fade-alpha-bottom))) 100%
      );
      mask-image: linear-gradient(
        to bottom,
        #000 0,
        #000 calc(100% - 32px),
        rgba(0, 0, 0, calc(1 - var(--fade-alpha-bottom))) 100%
      );
    }
  }

  @keyframes cardIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .card-enter {
      animation: none;
    }

    .card-scroll-body {
      transition: none;
    }
  }
</style>
